import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CalendarClock, ChevronRight, Users, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Prominent RED banner that surfaces the next active experience.
 * Auto-hides if there are no active/upcoming events. Kept compact so it
 * doesn't take over the home page — but visually loud so guests notice it.
 */
export default function ActiveExperienceBanner({ lang = "it", onDataReady }) {
  const [event, setEvent] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/experiences?only_active=true`)
      .then((r) => {
        const list = r.data.events || [];
        if (list.length > 0) {
          setEvent(list[0]);
          onDataReady?.(true);
        } else {
          onDataReady?.(false);
        }
      })
      .catch(() => onDataReady?.(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!event) return null;

  const today = new Date().toISOString().slice(0, 10);
  const isNow = event.active_from <= today && event.active_to >= today;
  const labelUp = isNow
    ? { it: "EVENTO IN CORSO", en: "LIVE EVENT", es: "EN CURSO", fr: "EN COURS", de: "AKTUELLES EVENT" }
    : { it: "PROSSIMO EVENTO", en: "NEXT EVENT", es: "PRÓXIMO EVENTO", fr: "PROCHAIN ÉVÉNEMENT", de: "NÄCHSTES EVENT" };
  const label = labelUp[lang] || labelUp.it;

  const fmtDate = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "long" });
  const dateStr = event.active_from === event.active_to
    ? fmtDate(event.active_from)
    : `${fmtDate(event.active_from)} → ${fmtDate(event.active_to)}`;

  return (
    <button
      onClick={() => navigate(`/eventi/${event.id}`)}
      data-testid="active-experience-banner"
      className="w-full block text-left mb-4 rounded-2xl overflow-hidden shadow-md ring-2 ring-red-500 hover:ring-red-600 transition-all hover:shadow-lg group"
    >
      <div className="relative bg-gradient-to-br from-red-700 via-red-600 to-red-800 text-white">
        {/* Decorative gold accent */}
        <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-amber-300/20 blur-2xl pointer-events-none" />
        <div className="flex gap-2.5 p-2 relative items-center">
          {event.image_url ? (
            <img
              src={event.image_url}
              alt=""
              className="w-11 h-11 rounded-lg object-cover shrink-0 ring-2 ring-white/30"
            />
          ) : (
            <div className="w-11 h-11 rounded-lg bg-red-500/40 flex items-center justify-center shrink-0 ring-2 ring-white/30">
              <Sparkles className="h-5 w-5 text-amber-200" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-[9px] uppercase tracking-widest font-black bg-amber-300 text-red-900 px-1.5 py-0.5 rounded-full">
                {label}
              </span>
              {isNow && <span className="w-2 h-2 rounded-full bg-amber-300 animate-pulse" />}
            </div>
            <h3 className="font-serif text-[13px] sm:text-sm font-bold leading-tight line-clamp-1 mt-0.5">{event.title}</h3>
            <div className="flex items-center gap-1.5 text-[10px] text-amber-50/90 flex-wrap">
              <span className="inline-flex items-center gap-1">
                <CalendarClock className="h-2.5 w-2.5" /> {dateStr}
              </span>
              <span>· {event.day_start_time}–{event.day_end_time}</span>
              {event.price > 0 && <span>· <b>{event.price} €</b></span>}
            </div>
          </div>
          <ChevronRight className="h-4 w-4 shrink-0 opacity-80 group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </button>
  );
}
